/**
 * Area elements API (AST-87) — manual project KMZ mapping.
 * No fixture fallback: a failed save must reach the user.
 */
import apiClient from './api';

export const fetchAreaElements = (projectId, { includeRetired = false } = {}) =>
  apiClient.get(`/projects/${projectId}/area-elements${includeRetired ? '?includeRetired=true' : ''}`);

/** Full replacement of the mapping. The server retires anything left out. */
export const saveAreaElements = (projectId, elements) =>
  apiClient.put(`/projects/${projectId}/area-elements`, { elements });

export const fetchAreaDistances = (projectId) =>
  apiClient.get(`/projects/${projectId}/area-distances`);

/** Same idea as countSlice.entryErrorMessage: prefer the server's { error }. */
export const areaErrorMessage = (error, fallback) => {
  if (error?.status === 0) return "Can't reach the server. Check your connection and try again.";
  if (error?.status === 401) return 'Your session has expired. Sign in again.';
  if (error?.status === 403) return "You don't have permission to change this project's areas.";
  return error?.data?.error || error?.data?.message || fallback;
};

// ---------------------------------------------------------------------------
// Stored KMZ/KML files (AST-98)
// ---------------------------------------------------------------------------

const kmzContentType = (name) => (/\.kml$/i.test(name) ? 'application/vnd.google-earth.kml+xml' : 'application/vnd.google-earth.kmz');

/**
 * Store a KMZ/KML file against the project:
 *   1. ask the API for a presigned PUT (it checks access, type and size)
 *   2. PUT the file straight to S3 -- it never passes through the API
 *   3. tell the API it's done; the API checks the object exists and records it
 * Resolves to the stored file record ({ uploadId, key, fileName, size, ... }).
 * fetchImpl is injectable for tests.
 */
export async function storeKmzFile(projectId, file, { fetchImpl = fetch } = {}) {
  const contentType = kmzContentType(file.name);
  const { uploadId, uploadUrl, headers } = await apiClient.post(`/projects/${projectId}/kmz-uploads`, {
    fileName: file.name,
    size: file.size,
    contentType,
  });
  const put = await fetchImpl(uploadUrl, { method: 'PUT', headers: headers || { 'Content-Type': contentType }, body: file });
  if (!put.ok) {
    const err = new Error(`Upload failed (${put.status})`);
    err.status = put.status;
    throw err;
  }
  const done = await apiClient.post(`/projects/${projectId}/kmz-uploads/${uploadId}/complete`, {});
  return done.item;
}

export const fetchKmzFiles = (projectId) => apiClient.get(`/projects/${projectId}/kmz-files`);

/**
 * Download a stored file through a presigned GET and return it as a File, so
 * it goes through the same parser as a fresh upload.
 */
export async function loadKmzFile(projectId, uploadId, { fetchImpl = fetch } = {}) {
  const { downloadUrl, fileName } = await apiClient.get(`/projects/${projectId}/kmz-files/${uploadId}/download`);
  const res = await fetchImpl(downloadUrl);
  if (!res.ok) {
    const err = new Error(`Download failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  const blob = await res.blob();
  return new File([blob], fileName, { type: kmzContentType(fileName) });
}

/** Tag each element with the stored file it came from (leaves others alone). */
export const withSourceFile = (elements, sourceFileKey) => (sourceFileKey
  ? elements.map((e) => (e.sourceFileKey ? e : { ...e, sourceFileKey }))
  : elements);

